/**
 * Area rank ladder — the shared 10-Rank progression for a domain's "areas"
 * (Personal's Disciplines: Breathing, Fitness, …). Areas level on a SHORT,
 * STEEP ladder (10 ranks) over the SAME economy XP that credits the domain —
 * "one action, two ledgers" (the area rank is a derived view; the domain keeps
 * its own 100-level curve). Because there are only 10 ranks, each rank takes
 * several domain-levels' worth of XP — modeled on breathwork's SMP curve but on
 * economy XP, and tuned to stay human-reachable (mastery, not a billion-XP wall).
 *
 * The thresholds are deliberately tunable — one table, every area.
 */

/** Cumulative economy XP needed to REACH each rank (index = rank, 0..10). */
export const AREA_RANK_THRESHOLDS = [
  0,      // Rank 0  — untrained
  600,    // Rank 1
  1600,   // Rank 2
  3200,   // Rank 3
  5600,   // Rank 4
  9000,   // Rank 5
  13500,  // Rank 6
  19500,  // Rank 7
  27500,  // Rank 8
  38000,  // Rank 9
  52000,  // Rank 10 — mastered
] as const;

export const MAX_AREA_RANK = AREA_RANK_THRESHOLDS.length - 1; // 10

export interface AreaRankInfo {
  /** 0..10 */
  rank: number;
  /** Total accumulated economy XP. */
  xp: number;
  /** XP earned within the current rank. */
  xpInRank: number;
  /** XP span of the current rank (0 at max). */
  xpForRank: number;
  /** XP remaining to the next rank (0 at max). */
  xpToNext: number;
  /** 0..100 progress within the current rank. */
  progress: number;
  isMax: boolean;
}

/** Resolve total area XP → its rank + within-rank progress. Pure + tunable. */
export function areaRank(xp: number): AreaRankInfo {
  const x = Math.max(0, isNaN(xp) ? 0 : xp);

  let rank = 0;
  for (let i = AREA_RANK_THRESHOLDS.length - 1; i >= 0; i--) {
    if (x >= AREA_RANK_THRESHOLDS[i]) {
      rank = i;
      break;
    }
  }

  if (rank >= MAX_AREA_RANK) {
    return { rank: MAX_AREA_RANK, xp: x, xpInRank: 0, xpForRank: 0, xpToNext: 0, progress: 100, isMax: true };
  }

  const start = AREA_RANK_THRESHOLDS[rank];
  const end = AREA_RANK_THRESHOLDS[rank + 1];
  const span = end - start;
  const xpInRank = x - start;
  const progress = span > 0 ? Math.min(100, Math.max(0, (xpInRank / span) * 100)) : 0;

  return { rank, xp: x, xpInRank, xpForRank: span, xpToNext: end - x, progress, isMax: false };
}

/**
 * Sum a set of breathwork (or any) sessions' economy XP with a streak
 * multiplier applied — the SINGLE definition of "breathwork's economy XP" so
 * the user total, the Personal domain credit, and the Breathing area rank all
 * agree on the same number (one XP → every ledger).
 */
export function sumEconomyXP(
  sessions: ReadonlyArray<{ xpEarned: number }>,
  streakMultiplier: number
): number {
  return sessions.reduce((sum, s) => sum + Math.round((s.xpEarned || 0) * streakMultiplier), 0);
}
